import React, { useState } from "react";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import {
  getAppMetricsSummary,
  getAppMetricsTotals,
  getAppMetricsGrowth,
  getAppMetricsCrashes,
  getAppMetricsByPlatform,
  runAppMetricsSync,
} from "../../helpers/fakebackend_helper";
import { useCachedRequest, invalidateCache } from "../../helpers/cachedFetch";
import ReviewsFeed from "./ReviewsFeed";
// Round a raw max up to a "nice" axis ceiling (1/2/5 × 10^n) so the Y-axis
// ticks land on readable numbers instead of e.g. 73.
const niceCeil = (n) => {
  if (!Number.isFinite(n) || n <= 5) return 5;
  const pow = Math.pow(10, Math.floor(Math.log10(n)));
  const f = n / pow;
  const nf = f <= 1 ? 1 : f <= 2 ? 2 : f <= 5 ? 5 : 10;
  return nf * pow;
};

// Inline bar chart — Chart.js dep was removed with the template purge, so this
// is a dependency-free SVG-less bar chart with a real Y-axis (downloads),
// gridlines and X-axis date labels.
// Accepts `{ labels: string[], datasets: [{ label, data: number[] }] }`.
const BarChart = ({ chartData }) => {
  const labels = (chartData && chartData.labels) || [];
  const datasets = (chartData && chartData.datasets) || [];
  if (labels.length === 0) {
    return <div className="h-full flex items-center justify-center text-gray-400">No data</div>;
  }
  const rawMax = Math.max(
    1,
    ...datasets.flatMap((d) => (d.data || []).map((v) => Number(v) || 0)),
  );
  const TICKS = 4;
  const axisMax = niceCeil(rawMax);
  // Y-axis tick values, top → bottom (axisMax … 0).
  const ticks = Array.from({ length: TICKS + 1 }, (_, k) => Math.round((axisMax / TICKS) * (TICKS - k)));
  // Show at most ~12 X-axis labels so dense windows (90 days) stay legible.
  const step = Math.max(1, Math.ceil(labels.length / 12));
  // "Jun 14, 2026" → "Jun 14"
  const axisDate = (label) => String(label).split(",")[0];

  return (
    <div className="h-full flex flex-col">
      <div className="flex-1 flex min-h-0">
        {/* Y axis labels (downloads) */}
        <div
          className="flex flex-col justify-between items-end text-[10px] text-gray-400 pr-2 py-1"
          style={{ width: 44 }}
        >
          {ticks.map((t, i) => (
            <div key={i} className="leading-none">{Number(t).toLocaleString()}</div>
          ))}
        </div>
        {/* Plot area */}
        <div className="flex-1 relative border-l border-b border-gray-700">
          {/* horizontal gridlines, aligned with the ticks above */}
          {ticks.map((t, i) => (
            <div
              key={i}
              className="absolute left-0 right-0 border-t border-gray-800/70"
              style={{ top: `${(i / TICKS) * 100}%` }}
            />
          ))}
          {/* bars */}
          <div className="absolute inset-0 flex items-end gap-1 px-1">
            {labels.map((label, i) => (
              <div key={i} className="flex-1 flex justify-center items-end gap-0.5 h-full min-w-0">
                {datasets.map((d, j) => {
                  const v = Number((d.data || [])[i] || 0);
                  const h = (v / axisMax) * 100;
                  const color = j === 0 ? "#5758FC" : "#2BD56F";
                  return (
                    <div
                      key={j}
                      title={`${axisDate(label)} — ${d.label || "downloads"}: ${v.toLocaleString()}`}
                      className="flex-1 rounded-t transition-all"
                      style={{
                        height: `${h}%`,
                        minHeight: v > 0 ? 2 : 0,
                        background: `linear-gradient(180deg, ${color}, ${color}aa)`,
                      }}
                    />
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </div>
      {/* X axis labels (dates), aligned to the plot area via the same 44px gutter */}
      <div className="flex pl-1 pr-1" style={{ marginLeft: 44 }}>
        {labels.map((label, i) => (
          <div
            key={i}
            className="flex-1 text-[9px] text-gray-400 text-center truncate min-w-0 pt-1"
          >
            {i % step === 0 ? axisDate(label) : ""}
          </div>
        ))}
      </div>
    </div>
  );
};

const GROWTH_DAYS_OPTIONS = [7, 30, 90];

const formatNumber = (num, decimals = 0) => {
  if (num == null || isNaN(num)) return "—";
  return Number(num).toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
};

const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  try {
    const d = new Date(dateStr);
    return isNaN(d.getTime()) ? dateStr : d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return dateStr;
  }
};

// Month + year only (no day/time), for the coverage-window label.
const formatMonthYear = (dateStr) => {
  if (!dateStr) return "—";
  try {
    const d = new Date(dateStr);
    return isNaN(d.getTime()) ? dateStr : d.toLocaleDateString("en-US", { year: "numeric", month: "short" });
  } catch {
    return dateStr;
  }
};

// Date + time, for the "last synced" stamp (the cron/manual run time, not the
// data-coverage date). Falls back gracefully on an unparseable value.
const formatDateTime = (dateStr) => {
  if (!dateStr) return null;
  try {
    const d = new Date(dateStr);
    return isNaN(d.getTime())
      ? dateStr
      : d.toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  } catch {
    return dateStr;
  }
};

const AppMetrics = ({ embedded = false } = {}) => {
  document.title = "App Metrics | BiorGov - Admin";

  const [syncing, setSyncing] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [growthDays, setGrowthDays] = useState(30);
  // Per-platform breakdown tables (Android + iOS) are collapsed by default;
  // "See more" under the Daily downloads board reveals them.
  const [showDetails, setShowDetails] = useState(false);

  // The axios response interceptor already unwraps the JSON body. The
  // /metrics/growth and /metrics/platform/:p endpoints intentionally return a
  // top-level `data` array as their payload — read it once, no second drill-in.
  const summaryReq = useCachedRequest({
    key: "app-metrics:summary",
    fetcher: () => getAppMetricsSummary()
      .then((res) => ({
        total_downloads: res?.total_downloads ?? 0,
        android_downloads: res?.android_downloads ?? null,
        ios_downloads: res?.ios_downloads ?? null,
        total_redownloads: res?.total_redownloads ?? 0,
        android_redownloads: res?.android_redownloads ?? null,
        ios_redownloads: res?.ios_redownloads ?? null,
        avg_crash_rate: res?.avg_crash_rate ?? 0,
        last_sync: res?.last_sync ?? null,
      }))
      .catch(() => ({ total_downloads: 0, android_downloads: null, ios_downloads: null, total_redownloads: 0, android_redownloads: null, ios_redownloads: null, avg_crash_rate: 0, last_sync: null })),
  });
  const summary = summaryReq.data;

  // Lifetime install total + live (active-device) count, read straight from each
  // store's own running totals — the "real" numbers, vs the 30-day window sum.
  const totalsReq = useCachedRequest({
    key: "app-metrics:totals",
    fetcher: () => getAppMetricsTotals()
      .then((res) => ({
        total_installs: res?.total_installs ?? null,
        active_devices: res?.active_devices ?? null,
        window: res?.window || null,
        android: res?.android || {},
        ios: res?.ios || {},
      }))
      .catch(() => ({ total_installs: null, active_devices: null, window: null, android: {}, ios: {} })),
  });
  const totals = totalsReq.data;

  // Distinguish "the store genuinely reported zero installs" from "we could not
  // reach the backend". The old `.catch(() => [])` collapsed both into an empty
  // array, so a 500 rendered as the same "No growth data" message as a real zero
  // — which is exactly how a broken store sync stayed invisible for 30 days.
  const growthReq = useCachedRequest({
    key: `app-metrics:growth:${growthDays}`,
    fetcher: () => getAppMetricsGrowth({ days: growthDays })
      .then((res) => ({ rows: Array.isArray(res?.data) ? res.data : [] }))
      .catch((err) => ({ rows: [], error: typeof err === "string" ? err : err?.message || "Request failed" })),
    deps: [growthDays],
  });
  const growth = growthReq.data?.rows || [];
  const growthError = growthReq.data?.error || null;

  const platformsReq = useCachedRequest({
    key: "app-metrics:platforms:90",
    fetcher: () => Promise.all([
      getAppMetricsByPlatform("android", { days: 90 }).catch(() => ({ data: [] })),
      getAppMetricsByPlatform("ios", { days: 90 }).catch(() => ({ data: [] })),
    ]).then(([a, i]) => ({
      android: Array.isArray(a?.data) ? a.data : [],
      ios: Array.isArray(i?.data) ? i.data : [],
    })),
  });
  const android = platformsReq.data?.android || [];
  const ios = platformsReq.data?.ios || [];

  // Crash/error stats from the PostHog `app_error` events we ingest (iOS +
  // Android + web) — the same live source that powers the /errors page. Fails
  // soft to null so the card shows "—" with a reason rather than erroring.
  const crashesReq = useCachedRequest({
    key: "app-metrics:crashes:7",
    fetcher: () => getAppMetricsCrashes({ days: 7 })
      .then((res) => ({
        days: res?.days ?? 7,
        fatal_crashes: res?.fatal_crashes ?? null,
        affected_users: res?.affected_users ?? null,
        android: res?.android || {},
        ios: res?.ios || {},
        status: res?.status || null,
      }))
      .catch(() => ({ days: 7, fatal_crashes: null, affected_users: null, android: {}, ios: {}, status: "error" })),
  });
  const crashes = crashesReq.data;
  const crashesReady = crashes?.status === "ok" && crashes?.fatal_crashes != null;
  // Friendly reason when the PostHog crash/error feed can't be read, so the card
  // explains the Play-vitals fallback instead of silently showing it.
  const crashHint = (status) => {
    if (!status || status === "ok") return null;
    if (String(status).startsWith("error:")) return String(status).replace(/^error:\s*/, "");
    return "Crash/error feed unavailable";
  };

  const loading = summaryReq.loading || totalsReq.loading || growthReq.loading || platformsReq.loading;

  const fetchAll = async () => {
    invalidateCache("app-metrics:");
    await Promise.all([summaryReq.refetch(), totalsReq.refetch(), growthReq.refetch(), platformsReq.refetch(), crashesReq.refetch()]);
  };

  // Which store(s) a totals figure came from, so the card can say so honestly
  // (iOS reports null until the Analytics API integration lands).
  const sourceNote = (key) => {
    const aOk = totals?.android?.[key] != null;
    const iOk = totals?.ios?.[key] != null;
    if (aOk && iOk) return "Android + iOS";
    if (aOk) return "Android · iOS pending";
    if (iOk) return "iOS · Android pending";
    return "Awaiting store data";
  };

  // Map the backend's raw `status` codes to a short human reason, so a blank
  // card explains itself instead of just showing "—". Android is the only
  // store that reports active_devices today (iOS is "not_implemented").
  const STATUS_REASONS = {
    ok: null,
    package_name_not_set: "Play package name not configured",
    bucket_not_set: "Play stats bucket not configured",
    creds_missing: "Play service-account creds missing",
    bucket_forbidden_403: "Service account lacks bucket read access",
    csv_not_found: "No export in bucket yet",
    csv_empty: "Store export is empty",
    csv_missing_columns: "Export missing the active-devices column",
    not_implemented: "iOS not wired yet",
  };
  const statusReason = (raw) => {
    if (!raw) return null;
    if (raw in STATUS_REASONS) return STATUS_REASONS[raw];
    if (String(raw).startsWith("error:")) return String(raw).replace(/^error:\s*/, "");
    return raw;
  };
  // For a null totals figure, prefer Android's reason (the active store), then iOS.
  const blankReason = () => {
    const a = statusReason(totals?.android?.status);
    if (a) return a;
    return statusReason(totals?.ios?.status);
  };

  // The Android-vs-Apple split row rendered beneath a headline number, so each
  // platform is visible separately, not just the sum. A null value (that store
  // hasn't reported yet) renders as "—".
  const PlatformSplit = ({ android, ios, androidNa, iosNa }) => (
    <div className="flex items-center justify-center gap-3 mt-2 text-[11px]">
      <span className="text-green-300/90 inline-flex items-center gap-1">
        <i className="ri-android-line"></i> Android {android == null ? (androidNa || "—") : formatNumber(android)}
      </span>
      <span className="text-gray-400 inline-flex items-center gap-1">
        <i className="ri-apple-line"></i> Apple {ios == null ? (iosNa || "—") : formatNumber(ios)}
      </span>
    </div>
  );

  const handleSync = async () => {
    setSyncing(true);
    setError(null);
    try {
      // Backfill the last 30 days so the tables/chart populate immediately
      // instead of accumulating one day per nightly cron run.
      await runAppMetricsSync({ days: 30 });
      await fetchAll();
    } catch (err) {
      setError(err?.message || String(err));
    } finally {
      setSyncing(false);
    }
  };

  // Fast, DB-only reload: re-reads whatever the backend already has (no slow
  // round-trip to Apple/Google like Sync does). Use this after running a
  // backfill/sync elsewhere — it finishes in a second or two.
  const handleRefresh = async () => {
    setRefreshing(true);
    setError(null);
    try {
      await fetchAll();
    } catch (err) {
      setError(err?.message || String(err));
    } finally {
      setRefreshing(false);
    }
  };

  const growthChartData = React.useMemo(() => {
    const list = growth || [];
    const sorted = [...list].sort((a, b) => new Date(a.date) - new Date(b.date));
    return {
      labels: sorted.map((d) => formatDate(d.date)),
      datasets: [
        { label: "Daily downloads", data: sorted.map((d) => Number(d.downloads) || 0) },
      ],
    };
  }, [growth]);

  // The most recent date the store data covers, across every source we hold —
  // answers "how current are these numbers?" so the single Sync button doesn't
  // leave you guessing. Null until something has loaded.
  const dataThrough = React.useMemo(() => {
    const dates = [];
    [growth, android, ios].forEach((arr) => (arr || []).forEach((d) => d?.date && dates.push(d.date)));
    if (totals?.android?.as_of) dates.push(totals.android.as_of);
    if (totals?.ios?.as_of) dates.push(totals.ios.as_of);
    if (!dates.length) return null;
    return dates.sort().slice(-1)[0];
  }, [growth, android, ios, totals]);

  return (
    <div className={embedded ? "" : "min-h-screen"}>
      <div className={embedded ? "w-full min-w-0" : "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8"}>
        {!embedded && <BreadCrumb title="App Metrics" pageTitle="Dashboard" />}

        <div className="mt-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6 gap-4">
            <h2 className="text-2xl font-light text-gray-100">
              Android &amp; iOS Store Metrics
            </h2>
            <div className="flex flex-wrap items-center gap-3">
              {/* Freshness indicator — answers "how current are these numbers?".
                  Two actions sit beside it: Refresh (instant DB re-read) and
                  Sync (slow live pull from Apple/Google). */}
              <span className="text-xs text-gray-500">
                {syncing
                  ? "Pulling latest from Apple & Google…"
                  : refreshing
                    ? "Reloading…"
                    : summary?.last_sync
                      ? `Last synced ${formatDateTime(summary.last_sync)}`
                      : dataThrough
                        ? `Data through ${formatDate(dataThrough)}`
                        : "No store data yet"}
              </span>
              {/* Refresh: re-reads the DB only — instant. Distinct from Sync,
                  which hits the live store APIs and can take many seconds. */}
              <button
                type="button"
                onClick={handleRefresh}
                disabled={refreshing || syncing || loading}
                title="Reload the numbers already in the database (fast — no live store call)"
                className="px-4 py-2 text-sm font-normal text-gray-200 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <i className={`ri-refresh-line ${refreshing ? "animate-spin" : ""}`}></i>
                <span>{refreshing ? "Refreshing…" : "Refresh"}</span>
              </button>
              <button
                type="button"
                onClick={handleSync}
                disabled={syncing || refreshing || loading}
                title="Pull the latest downloads, installs and active devices from the App Store and Google Play, then reload"
                className="px-4 py-2 text-sm font-normal text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <i className={`ri-refresh-line ${syncing ? "animate-spin" : ""}`}></i>
                <span>{syncing ? "Syncing…" : "Sync from stores"}</span>
              </button>
            </div>
          </div>

          {error && (
            <div className="mb-4 p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
              <div className="flex items-center gap-2 text-red-300">
                <i className="ri-error-warning-line"></i>
                <span className="text-sm">{error}</span>
              </div>
            </div>
          )}

          {loading && !summary ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[1, 2].map((key) => (
                <div
                  key={key}
                  className="bl-glass p-6 shadow-sm animate-pulse"
                >
                  <div className="h-4 bg-gray-700 rounded w-24 mb-4" />
                  <div className="h-10 bg-gray-700 rounded w-32" />
                </div>
              ))}
            </div>
          ) : (
            <>
              {/* Summary cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6 mb-8">
                {/* Lifetime total installs — the "real" all-time number */}
                <div className="bl-glass p-6 shadow-sm hover:shadow-md transition-shadow h-full flex flex-col items-center text-center">
                  <div className="w-12 h-12 rounded-xl bg-cyan-500/16 flex items-center justify-center mb-3">
                    <i className="ri-download-2-line text-2xl text-cyan-300"></i>
                  </div>
                  <p className="text-sm font-medium text-gray-400">Total Installs</p>
                  <p className="text-3xl font-semibold text-gray-100 mt-1">
                    {formatNumber(totals?.total_installs)}
                  </p>
                  <p className="text-[11px] text-gray-500 mt-1">
                    First-time + re-downloads, both stores
                    {totals?.window?.since ? ` · since ${formatMonthYear(totals.window.since)}` : ""} · not live users
                  </p>
                  <PlatformSplit android={totals?.android?.total_installs} ios={totals?.ios?.total_installs} />
                  {totals?.android?.all_time_installs != null && (
                    <p className="text-[11px] text-gray-500 mt-1">
                      <i className="ri-android-line text-green-300/90"></i> All-time (Play): {formatNumber(totals.android.all_time_installs)}
                    </p>
                  )}
                  {totals?.total_installs == null && blankReason() && (
                    <p className="text-[11px] text-amber-400/90 mt-1">
                      <i className="ri-error-warning-line"></i> {blankReason()}
                    </p>
                  )}
                </div>
                {/* Individual (first-time) installs — the unique-new-user count.
                    Replaces the old "New Installs" card; pairs with Re-downloads
                    to add up to Total Installs. */}
                <div className="bl-glass p-6 shadow-sm hover:shadow-md transition-shadow h-full flex flex-col items-center text-center">
                  <div className="w-12 h-12 rounded-xl bg-indigo-500/16 flex items-center justify-center mb-3">
                    <i className="ri-user-add-line text-2xl text-indigo-300"></i>
                  </div>
                  <p className="text-sm font-medium text-gray-400">Individual installs</p>
                  <p className="text-3xl font-semibold text-gray-100 mt-1">
                    {formatNumber(summary?.total_downloads ?? 0)}
                  </p>
                  <p className="text-[11px] text-gray-500 mt-1">First-time downloads only (unique new users)</p>
                  <PlatformSplit android={summary?.android_downloads} ios={summary?.ios_downloads} />
                </div>
                {/* Re-downloads / reinstalls — the other half of Total Installs.
                    iOS from the Sales report's "3*" product types; Android from
                    Play's "Install events" minus first-time "Daily User Installs". */}
                <div className="bl-glass p-6 shadow-sm hover:shadow-md transition-shadow h-full flex flex-col items-center text-center">
                  <div className="w-12 h-12 rounded-xl bg-fuchsia-500/16 flex items-center justify-center mb-3">
                    <i className="ri-restart-line text-2xl text-fuchsia-300"></i>
                  </div>
                  <p className="text-sm font-medium text-gray-400">Re-downloads</p>
                  <p className="text-3xl font-semibold text-gray-100 mt-1">
                    {formatNumber(summary?.total_redownloads ?? 0)}
                  </p>
                  <p className="text-[11px] text-gray-500 mt-1">Reinstalls by returning users (both stores)</p>
                  <PlatformSplit android={summary?.android_redownloads} ios={summary?.ios_redownloads} />
                </div>
                {/* Live users = current active-device install base (a snapshot, not a sum) */}
                <div className="bl-glass p-6 shadow-sm hover:shadow-md transition-shadow h-full flex flex-col items-center text-center">
                  <div className="w-12 h-12 rounded-xl bg-emerald-500/16 flex items-center justify-center mb-3">
                    <i className="ri-user-follow-line text-2xl text-emerald-300"></i>
                  </div>
                  <p className="text-sm font-medium text-gray-400">Live Users</p>
                  <p className="text-3xl font-semibold text-gray-100 mt-1">
                    {formatNumber(totals?.active_devices)}
                  </p>
                  <p className="text-[11px] text-gray-500 mt-1">Active devices · {sourceNote("active_devices")}</p>
                  <PlatformSplit android={totals?.android?.active_devices} ios={totals?.ios?.active_devices} />
                  {totals?.active_devices == null && blankReason() && (
                    <p className="text-[11px] text-amber-400/90 mt-1">
                      <i className="ri-error-warning-line"></i> {blankReason()}
                    </p>
                  )}
                  {totals?.active_devices != null && totals?.ios?.active_devices == null && (
                    <p className="text-[11px] text-gray-500 mt-1">
                      Apple shows once enough users opt into App Analytics
                    </p>
                  )}
                </div>
                <div className="bl-glass p-6 shadow-sm hover:shadow-md transition-shadow h-full flex flex-col items-center text-center">
                  <div className="w-12 h-12 rounded-xl bg-amber-500/16 flex items-center justify-center mb-3">
                    <i className="ri-bug-line text-2xl text-amber-300"></i>
                  </div>
                  <p className="text-sm font-medium text-gray-400">App crashes</p>
                  {crashesReady ? (
                    <>
                      {/* PostHog app_error events — real iOS + Android source */}
                      <p className="text-3xl font-semibold text-gray-100 mt-1">
                        {formatNumber(crashes.fatal_crashes)}
                      </p>
                      <p className="text-[11px] text-gray-500 mt-1">
                        Errors · last {crashes.days || 7}d · {formatNumber(crashes.affected_users ?? 0)} users · PostHog
                      </p>
                      <PlatformSplit android={crashes.android?.fatal_crashes} ios={crashes.ios?.fatal_crashes} />
                    </>
                  ) : (
                    <>
                      {/* Fallback if the PostHog feed is empty: Android Play vitals rate */}
                      <p className="text-3xl font-semibold text-gray-100 mt-1">
                        {summary?.avg_crash_rate ? `${(Number(summary.avg_crash_rate) * 100).toFixed(2)}%` : "—"}
                      </p>
                      <p className="text-[11px] text-gray-500 mt-1">Android crash rate · Play vitals</p>
                      {crashHint(crashes?.status) && (
                        <p className="text-[11px] text-amber-400/90 mt-1">
                          <i className="ri-error-warning-line"></i> {crashHint(crashes?.status)}
                        </p>
                      )}
                    </>
                  )}
                </div>
              </div>

              {/* Growth chart */}
              <div className="bl-glass p-6 shadow-sm mb-8">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                  <h3 className="text-lg font-medium text-gray-100">Daily downloads</h3>
                  <div className="flex rounded-lg border border-gray-700 overflow-hidden">
                    {GROWTH_DAYS_OPTIONS.map((d) => (
                      <button
                        key={d}
                        type="button"
                        onClick={() => setGrowthDays(d)}
                        className={`px-4 py-2 text-sm font-medium transition-colors ${
                          growthDays === d
                            ? "bg-bl-accent text-white"
                            : "bg-gray-800 text-gray-300 hover:bg-bl-accent/10"
                        }`}
                      >
                        {d} days
                      </button>
                    ))}
                  </div>
                </div>
                {growthChartData.labels.length > 0 ? (
                  <div className="h-80">
                    <BarChart
                      dataColors='["#06B6D4", "#10B981"]'
                      chartData={growthChartData}
                    />
                  </div>
                ) : growthError ? (
                  <div className="h-80 flex flex-col items-center justify-center text-center gap-1 px-4">
                    <span className="text-red-400">Couldn’t load growth data</span>
                    <span className="text-gray-400 text-xs">{growthError}</span>
                    <span className="text-gray-500 text-xs">
                      This is a request failure, not a report of zero installs.
                    </span>
                  </div>
                ) : (
                  <div className="h-80 flex flex-col items-center justify-center text-center gap-1 px-4">
                    <span className="text-gray-400">No installs recorded for the selected period.</span>
                    <span className="text-gray-500 text-xs">
                      The stores reported zero. If that looks wrong, re-run “Sync from stores” and
                      check the sync status — both fetchers fail soft to 0 when credentials are missing.
                    </span>
                  </div>
                )}
              </div>

              {/* Per-platform breakdown — collapsed by default behind "See more" */}
              <div className="mb-8">
                <button
                  type="button"
                  onClick={() => setShowDetails((v) => !v)}
                  className="px-4 py-2 text-sm font-normal text-gray-300 bg-gray-800 border border-gray-700 rounded-lg hover:bg-bl-accent/10 transition-colors flex items-center gap-2"
                >
                  <i className={`ri-arrow-down-s-line transition-transform ${showDetails ? "rotate-180" : ""}`}></i>
                  <span>{showDetails ? "See less" : "See more — daily breakdown by platform"}</span>
                </button>

                {showDetails && (
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-8 mt-6">
                {/* Android */}
                <div className="bl-glass overflow-hidden">
                  <div className="px-6 py-4 border-b border-gray-700 bg-gradient-to-r from-green-500/10 to-emerald-500/10">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-green-500 flex items-center justify-center">
                        <i className="ri-android-line text-xl text-white"></i>
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-100">Android</h3>
                        <p className="text-sm text-gray-400">Google Play metrics</p>
                      </div>
                    </div>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-bl-accent/10 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                          <th className="px-4 py-3">Date</th>
                          <th className="px-4 py-3 text-right">Downloads</th>
                          <th className="px-4 py-3 text-right">Active devices</th>
                          <th className="px-4 py-3 text-right">Crash rate</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-700">
                        {android.length === 0 ? (
                          <tr>
                            <td colSpan={4} className="px-4 py-8 text-center text-gray-400">
                              No Android data yet. Run &quot;Sync from stores&quot; to fetch.
                            </td>
                          </tr>
                        ) : (
                          android.map((row, idx) => (
                            <tr key={`${row.date}-${idx}`} className="hover:bg-bl-accent/10/30">
                              <td className="px-4 py-3 text-gray-100">{formatDate(row.date)}</td>
                              <td className="px-4 py-3 text-right text-gray-300">{formatNumber(row.downloads)}</td>
                              <td className="px-4 py-3 text-right text-gray-300">{formatNumber(row.active_users)}</td>
                              <td className="px-4 py-3 text-right text-gray-300">
                                {row.crash_rate != null ? `${(Number(row.crash_rate) * 100).toFixed(2)}%` : "—"}
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* iOS */}
                <div className="bl-glass overflow-hidden">
                  <div className="px-6 py-4 border-b border-gray-700 bg-gradient-to-r from-gray-800 to-slate-900">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-gray-700 flex items-center justify-center">
                        <i className="ri-apple-line text-xl text-white"></i>
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-100">iOS</h3>
                        <p className="text-sm text-gray-400">App Store Connect metrics</p>
                      </div>
                    </div>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-bl-accent/10 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                          <th className="px-4 py-3">Date</th>
                          <th className="px-4 py-3 text-right">Downloads</th>
                          <th className="px-4 py-3 text-right">Active users (n/a)</th>
                          <th className="px-4 py-3 text-right">Crash rate (n/a)</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-700">
                        {ios.length === 0 ? (
                          <tr>
                            <td colSpan={4} className="px-4 py-8 text-center text-gray-400">
                              No iOS data yet. Run &quot;Sync from stores&quot; to fetch.
                            </td>
                          </tr>
                        ) : (
                          ios.map((row, idx) => (
                            <tr key={`${row.date}-${idx}`} className="hover:bg-bl-accent/10/30">
                              <td className="px-4 py-3 text-gray-100">{formatDate(row.date)}</td>
                              <td className="px-4 py-3 text-right text-gray-300">{formatNumber(row.downloads)}</td>
                              <td className="px-4 py-3 text-right text-gray-300">{formatNumber(row.active_users)}</td>
                              <td className="px-4 py-3 text-right text-gray-300">
                                {row.crash_rate != null ? `${(Number(row.crash_rate) * 100).toFixed(2)}%` : "—"}
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
                )}
              </div>

              {/* App reviews — moved here from the standalone Reviews page */}
              <ReviewsFeed />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AppMetrics;
